import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';
import _ from 'underscore';

import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { FieldArray } from '~src/settings/FormikWrapperForStubbing';
import { applyColor }from '~src/style/ColorService';

import { DataConnectPoolDomainsSectionHeaders } from '../dataConnectNetworkAllowlist/DataConnectPoolDomainsSectionHeaders';
import { DataConnectPoolDomainsTableRow } from '../dataConnectNetworkAllowlist/DataConnectPoolDomainsTableRow';
import { Domain } from '../types/Domain';
import { DomainType } from '../types/DomainType';
import { PoolID } from '../types/PoolID';
import { UserDefinedPoolModel } from '../types/PoolModel';

export type MultiPoolTableEntryDomainsProps = Readonly<{
  poolId: PoolID;
  poolName: string;
  domains: Domain[];
  siteId: string;
  userDefinedPools: Record<string, UserDefinedPoolModel>;
  bridgeDomains: Record<string, Domain>;
  refreshPoolsAndClusters: () => void;
}>;

export const POOL_NODE_FIELD = 'settings.siteAdminDataConnectSettings.poolNodeSettings.nodes';

export function DataConnectMultiPoolTableEntryDomains(props: MultiPoolTableEntryDomainsProps) {
  // Filter domains based on feature flag
  const filteredDomains = React.useMemo(() => {
    if (!FeatureFlags.enableDataConnectFlatFile) {
      return _.filter(props.domains, domain => domain.domainType === DomainType.DOMAIN);
    }
    return props.domains;
  }, [props.domains]);

  const renderEmptyState = () => (
    <tr>
      <td colSpan={3} style={{ paddingLeft: 0 }}>
        <div className="tb-data-grid-cell-text-data-connect tb-data-grid-DataConnect-center">
          {Messages.dataConnectSiteSettingsPoolDomainDefaultRowDescription()}
        </div>
      </td>
    </tr>
  );

  const renderDomainRows = () => (
    filteredDomains.map((domain: Domain, index: number) => (
      <tr key={index}>
        <DataConnectPoolDomainsTableRow
          domain={domain}
          index={index}
          poolTestId={`pool-${props.poolId}-`}
          poolId={props.poolId}
          poolName={props.poolName}
          userDefinedPools={props.userDefinedPools}
          bridgeDomains={props.bridgeDomains}
          refreshPoolsAndClusters={props.refreshPoolsAndClusters}
        />
      </tr>
    ))
  );

  return (
    <table className={multiPoolDomainRowTableStyle}>
      {filteredDomains.length > 0 && <DataConnectPoolDomainsSectionHeaders />}
      <tbody>
        <FieldArray
          name={POOL_NODE_FIELD}
          render={() => (
            filteredDomains.length === 0
              ? renderEmptyState()
              : renderDomainRows()
          )}
        />
      </tbody>
    </table>
  );
}

export const multiPoolDomainRowTableStyle = style({
  borderSpacing: 0,
  textAlign: 'left',
  backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.default, TabStyles.Colors.White),
  cursor: 'default',
  paddingTop: 16,
  paddingBottom: 24,
  width: '100%'
});